import { useState } from 'react';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const services = [
    {
      icon: 'ri-road-map-line',
      title: 'Asphalt Repair',
      description: 'Professional asphalt repair services for commercial and residential properties to restore and maintain your pavement\'s integrity and appearance.',
      features: ['Surface restoration', 'Structural repairs', 'Long-lasting results']
    },
    {
      icon: 'ri-alert-line',
      title: 'Pot Holes',
      description: 'Expert pothole repair for commercial and residential properties to eliminate hazards and prevent further damage to your asphalt surfaces.',
      features: ['Quick repairs', 'Permanent solutions', 'Safety first']
    },
    {
      icon: 'ri-tools-line',
      title: 'Patches',
      description: 'High-quality patching services for commercial and residential damaged areas, ensuring seamless integration with existing pavement.',
      features: ['Precision work', 'Durable materials', 'Smooth finish']
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Sealers',
      description: 'Premium seal coating for commercial and residential properties to protect your asphalt from weather, UV rays, and daily wear and tear.',
      features: ['Weather protection', 'Extended lifespan', 'Enhanced appearance']
    },
    {
      icon: 'ri-fire-line',
      title: 'Hot Crack Filling',
      description: 'Professional hot crack filling for commercial and residential properties to prevent water infiltration and stop cracks from expanding.',
      features: ['Prevents water damage', 'Stops crack growth', 'Professional grade']
    },
    {
      icon: 'ri-roadster-line',
      title: 'Paving',
      description: 'Complete paving solutions for commercial and residential properties, including new installations and full surface replacements with premium materials.',
      features: ['New installations', 'Full replacements', 'Quality materials']
    }
  ];

  const projects = [
    {
      title: 'Commercial Plaza Seal Coating',
      location: 'New Brunswick, NJ',
      image: 'https://public.readdy.ai/ai/img_res/4fa8a4556b001c0d07188cd320261a7e.jpg'
    },
    {
      title: 'Residential Driveway Repair',
      location: 'Princeton, NJ',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20residential%20driveway%20with%20fresh%20seal%20coating%20smooth%20black%20asphalt%20surface%20suburban%20home%20well%20maintained%20property%20professional%20paving%20work%20clean%20edges%20central%20new%20jersey%20neighborhood&width=800&height=600&seq=proj2&orientation=landscape'
    },
    {
      title: 'Industrial Parking Lot',
      location: 'Edison, NJ',
      image: 'https://readdy.ai/api/search-image?query=large%20industrial%20parking%20lot%20with%20professional%20seal%20coating%20smooth%20black%20asphalt%20clear%20parking%20lines%20warehouse%20facility%20commercial%20property%20high%20quality%20paving%20work%20central%20new%20jersey%20industrial%20area&width=800&height=600&seq=proj3&orientation=landscape'
    },
    {
      title: 'Shopping Center Maintenance',
      location: 'Somerset, NJ',
      image: 'https://readdy.ai/api/search-image?query=shopping%20center%20parking%20lot%20with%20professional%20seal%20coating%20fresh%20black%20asphalt%20smooth%20surface%20retail%20complex%20modern%20commercial%20property%20central%20new%20jersey%20high%20quality%20paving%20services&width=800&height=600&seq=proj4&orientation=landscape'
    }
  ];

  const testimonials = [
    {
      name: 'Michael Rodriguez',
      role: 'Property Manager',
      company: 'Brunswick Plaza',
      text: 'Outstanding service! They completed our commercial parking lot seal coating ahead of schedule. The quality is exceptional and our tenants are thrilled with the results.',
      rating: 5
    },
    {
      name: 'Sarah Thompson',
      role: 'Homeowner',
      company: 'Princeton',
      text: 'My driveway looks brand new! The team was professional, efficient, and the pricing was very fair. Highly recommend their services.',
      rating: 5
    },
    {
      name: 'David Chen',
      role: 'Facilities Director',
      company: 'Edison Industrial Park',
      text: 'We\'ve used their services for multiple properties. Always reliable, professional, and the work quality is consistently excellent.',
      rating: 5
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4gh182jg2jl50j1ruo0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-gray-900 w-full top-0 z-50 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center py-6">
            <h1 className="flex items-center mb-4">
              <img 
                src="https://static.readdy.ai/image/dcfc6d853c933caf7a21050f65e4344a/7a2885cc4239e761e0751af18e48eaf8.png" 
                alt="Seal Coating J&D LLC" 
                className="h-44 md:h-64 w-auto"
              />
            </h1>
            
            <div className="flex flex-wrap items-center justify-center gap-6">
              <a href="#home" className="text-gray-300 hover:text-blue-900 transition-colors font-medium">Home</a>
              <a href="#services" className="text-gray-300 hover:text-blue-900 transition-colors font-medium">Services</a>
              <a href="#projects" className="text-gray-300 hover:text-blue-900 transition-colors font-medium">Projects</a>
              <a href="#testimonials" className="text-gray-300 hover:text-blue-900 transition-colors font-medium">Reviews</a>
              <a href="tel:7329840376" className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors font-bold text-lg">
                <i className="ri-phone-line"></i>
                732.984.0376
              </a>
              <a href="#contact" className="bg-blue-900 text-white px-6 py-3 rounded-full hover:bg-blue-800 transition-colors font-semibold whitespace-nowrap">
                Free Quote
              </a>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-900 border-t border-gray-800">
            <div className="px-4 py-4 space-y-3">
              <a href="#home" className="block text-gray-300 hover:text-blue-900 transition-colors font-medium py-2">Home</a>
              <a href="#services" className="block text-gray-300 hover:text-blue-900 transition-colors font-medium py-2">Services</a>
              <a href="#projects" className="block text-gray-300 hover:text-blue-900 transition-colors font-medium py-2">Projects</a>
              <a href="#testimonials" className="block text-gray-300 hover:text-blue-900 transition-colors font-medium py-2">Reviews</a>
              <a href="tel:7329840376" className="block text-blue-400 hover:text-blue-300 transition-colors font-bold py-2">
                <i className="ri-phone-line mr-2"></i>
                732.984.0376
              </a>
              <a href="#contact" className="block bg-blue-900 text-white px-6 py-3 rounded-full hover:bg-blue-800 transition-colors font-semibold text-center whitespace-nowrap">
                Free Quote
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-gray-900">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://static.readdy.ai/image/dcfc6d853c933caf7a21050f65e4344a/e2675f11c0f09c2d21c3cec2165f7052.png"
            alt="Background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-blue-900/20 border border-blue-900 rounded-full px-6 py-2 mb-8">
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">SEAL COATING J&D LLC - COMMERCIAL AND RESIDENTIAL SERVICES</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Expert Seal Coating &amp; Asphalt Solutions
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
              Transform and protect your pavement with professional seal coating, repairs, and maintenance services for commercial and residential properties trusted by businesses and homeowners across Central NJ.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="bg-blue-900 text-white px-10 py-5 rounded-full hover:bg-blue-800 transition-all font-bold text-lg text-center whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                Get Free Estimate
              </a>
              <a href="tel:7329840376" className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-10 py-5 rounded-full hover:bg-white/20 transition-all font-bold text-lg text-center whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-phone-line"></i>
                732.984.0376
              </a>
            </div>
            
            <div className="grid grid-cols-3 gap-8 mt-20 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">15+</div>
                <div className="text-gray-400 font-medium">Years Experience</div>
              </div>
              <div className="text-center border-l border-r border-gray-700">
                <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">500+</div>
                <div className="text-gray-400 font-medium">Projects Done</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">100%</div>
                <div className="text-gray-400 font-medium">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-50 rounded-full px-6 py-2 mb-4">
              <span className="text-blue-900 font-bold text-sm uppercase tracking-wider">What We Do</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Professional Asphalt Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive commercial and residential solutions for all your pavement needs in Central New Jersey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:border-blue-900 transition-all hover:shadow-2xl">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <div className="w-5 h-5 bg-blue-50 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-check-line text-blue-900 text-sm"></i>
                      </div>
                      <span className="font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block bg-blue-900/20 border border-blue-900 rounded-full px-6 py-2 mb-6">
                <span className="text-blue-400 font-bold text-sm uppercase tracking-wider">Why Choose Us</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Central NJ's Most Trusted Commercial and Residential Seal Coating Experts
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                SEALCOATING J&D LLC has become one of New Jersey's leading companies in asphalt driveway and parking lot maintenance and repair. We provide comprehensive commercial and residential services including crack sealing, sealcoating, pothole repair, and both preventive and corrective pavement maintenance for residential and commercial properties.

We are known for our quick response, effective solutions, and strong commitment to quality, fair costs, and customer satisfaction. Our goal is to help property owners, facility managers, and contractors improve the safety, appearance, value, and longevity of their paved surfaces while minimizing and solving any pavement-related issues.

Our professional and trained team takes pride in their work and has the equipment needed to handle any commercial or residential project. Thanks to our dedication and capability, our client base continues to grow. We are always available to answer calls, texts, or emails (732-984-0376 / sealcoatingd@gmail.com
), providing courteous service and delivering our best on every job.

We offer highly competitive pricing, because we believe clients should save time and money while enhancing their properties and communities.

SEALCOATING J&D LLC is fully licensed and insured. We strive to make every driveway or parking lot project as smooth and simple as possible, working with you personally from start to finish to ensure your needs are met with expertise and prompt attention.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Licensed &amp; Insured</h3>
                    <p className="text-gray-400">Fully certified professionals with comprehensive insurance coverage for your peace of mind.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-medal-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Premium Materials</h3>
                    <p className="text-gray-400">We use only the highest quality sealers and materials for long-lasting, durable results.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">24/7 Support</h3>
                    <p className="text-gray-400">Emergency services available with quick response times for urgent repairs.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-900 to-blue-700 rounded-3xl opacity-20 blur-2xl"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20asphalt%20seal%20coating%20crew%20working%20on%20commercial%20parking%20lot%20modern%20equipment%20workers%20in%20safety%20gear%20applying%20fresh%20sealant%20to%20smooth%20black%20pavement%20central%20new%20jersey%20location%20sunny%20day%20high%20quality%20professional%20paving%20services%20team%20at%20work&width=800&height=1000&seq=about2&orientation=portrait" 
                  alt="Professional team at work"
                  className="relative w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
                />
              </div>

              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-900 to-blue-700 rounded-3xl opacity-20 blur-2xl"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-blue-900">
                  <div className="relative" style={{ paddingBottom: '75%' }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src="https://www.youtube.com/embed/eGxrXvJ-Lwc?autoplay=1&mute=1&loop=1&playlist=eGxrXvJ-Lwc"
                      title="Seal Coating J&D LLC Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-50 rounded-full px-6 py-2 mb-4">
              <span className="text-blue-900 font-bold text-sm uppercase tracking-wider">Our Work</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Recent Commercial and Residential Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See the quality and craftsmanship we bring to every commercial and residential project across Central New Jersey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
                  <div className="flex items-center text-blue-300">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span className="font-medium">{project.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-50 rounded-full px-6 py-2 mb-4">
              <span className="text-blue-900 font-bold text-sm uppercase tracking-wider">Testimonials</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it - hear from satisfied customers across Central NJ
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:border-blue-900 transition-all hover:shadow-xl">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-blue-900 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role} - {testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <div className="inline-block bg-blue-900/20 border border-blue-900 rounded-full px-6 py-2 mb-6">
                <span className="text-blue-400 font-bold text-sm uppercase tracking-wider">Get In Touch</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Request Your Free Quote
              </h2>
              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                Ready to transform your Driveways? Get a free, no-obligation estimate from Central New Jersey's trusted commercial and residential seal coating experts.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Service Area</h3>
                    <p className="text-gray-400">Central New Jersey</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Phone</h3>
                    <a href="tel:7329840376" className="text-blue-400 hover:text-blue-300 font-bold">732.984.0376</a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Email</h3>
                    <p className="text-gray-400">sealcoatingd@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Business Hours</h3>
                    <p className="text-gray-400">Monday - Saturday: 7:00 AM - 6:00 PM</p>
                    <p className="text-gray-400">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-bold text-gray-900 mb-2">
                    Service Needed *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 pr-8 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all text-sm appearance-none cursor-pointer"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236b7280\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1.5em 1.5em' }}
                  >
                    <option value="">Select a service</option>
                    <option value="Asphalt Repair">Asphalt Repair</option>
                    <option value="Pot Holes">Pot Holes</option>
                    <option value="Patches">Patches</option>
                    <option value="Sealers">Sealers</option>
                    <option value="Hot Crack Filling">Hot Crack Filling</option>
                    <option value="Paving">Paving</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2">
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Tell us about your project..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-5 rounded-xl hover:from-blue-800 hover:to-blue-700 transition-all font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Get Free Quote'}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-500 text-green-800 px-4 py-4 rounded-xl">
                    <div className="flex items-center">
                      <i className="ri-check-line text-2xl mr-3"></i>
                      <span className="font-semibold">Thank you! We'll contact you soon.</span>
                    </div>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-500 text-red-800 px-4 py-4 rounded-xl">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-2xl mr-3"></i>
                      <span className="font-semibold">Something went wrong. Please try again.</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Protect Your Commercial or Residential Investment Today
          </h2>
          <p className="text-xl text-blue-50 mb-10 max-w-3xl mx-auto leading-relaxed">
            Don't let small cracks become costly repairs. Get professional seal coating and asphalt maintenance for your commercial or residential property from Central New Jersey's most trusted experts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact" className="inline-block bg-white text-blue-900 px-10 py-5 rounded-full hover:bg-gray-100 transition-all font-bold text-lg whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              Request Free Quote
            </a>
            <a href="tel:7329840376" className="inline-block bg-blue-800 text-white px-10 py-5 rounded-full hover:bg-blue-700 transition-all font-bold text-lg whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              <i className="ri-phone-line mr-2"></i>
              732.984.0376
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <h3 className="mb-4">
                <img 
                  src="https://static.readdy.ai/image/dcfc6d853c933caf7a21050f65e4344a/7a2885cc4239e761e0751af18e48eaf8.png" 
                  alt="Seal Coating J&D LLC" 
                  className="h-22 md:h-32 w-auto"
                />
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Central New Jersey's premier commercial and residential seal coating and asphalt repair specialists. Quality work, guaranteed results.
              </p>
              <div className="flex space-x-4">
                <a href="https://www.facebook.com/profile.php?id=100082926262150" target="_blank" rel="noopener noreferrer" className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <i className="ri-facebook-fill text-2xl text-white"></i>
                </a>
                <a href="https://www.instagram.com/sealcoatingjd" target="_blank" rel="noopener noreferrer" className="w-16 h-16 bg-gradient-to-br from-pink-500 to-orange-400 rounded-full flex items-center justify-center hover:from-pink-600 hover:to-orange-500 transition-colors">
                  <i className="ri-instagram-line text-2xl text-white"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-3">
                <li><a href="#services" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Asphalt Repair</a></li>
                <li><a href="#services" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Pot Holes</a></li>
                <li><a href="#services" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Patches</a></li>
                <li><a href="#services" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Sealers</a></li>
                <li><a href="#services" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Hot Crack Filling</a></li>
                <li><a href="#services" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Paving</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#home" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Home</a></li>
                <li><a href="#services" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Services</a></li>
                <li><a href="#projects" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Projects</a></li>
                <li><a href="#testimonials" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Reviews</a></li>
                <li><a href="#contact" className="hover:text-blue-400 transition-colors flex items-center"><i className="ri-arrow-right-s-line mr-1"></i>Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold text-lg mb-4">Contact Info</h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-map-pin-line text-blue-400 mr-3 mt-1 text-xl"></i>
                  <span>Central New Jersey</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-phone-line text-blue-400 mr-3 text-xl"></i>
                  <a href="tel:7329840376" className="text-blue-400 hover:text-blue-300 font-bold">732.984.0376</a>
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line text-blue-400 mr-3 text-xl"></i>
                  <span>sealcoatingd@gmail.com</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-blue-400 mr-3 mt-1 text-xl"></i>
                  <div>
                    <div>Mon-Sat: 7AM-6PM</div>
                    <div>Sunday: Closed</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Seal Coating J&D LLC. All rights reserved.
            </p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors text-sm font-medium">
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}